import {
  type Client,
  type ClientResponse,
  type Conversation,
  type ConversationChannel,
  type ConversationContext,
  type Observability,
  type RequestAction,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  InlineDataEventBus,
  type InlineDataService,
} from '@stripe-internal/inline-data';

import type {Api} from '../../core/lib/api/api';
import type {AppConfig, AppProfile, MessagePartClient} from '../../core/types';
import {buildV1Clients} from './buildV1Clients';
import {buildV1ConversationsControllerApi} from './buildV1ConversationsControllerApi';
import {buildV2Clients} from './buildV2Clients';
import {clearStoredConversation, clearStoredConversations} from './storage';
import type {
  ControllerError,
  ControllerResult,
  ConversationsControllerApi,
  CreateConversationParams,
  ListConversationFilters,
} from './types';
import type {
  ConversationLoadingOperation,
  ConversationsState,
  ConversationsStateStore,
  LegacyConversationsController,
} from '../app/types';
import {newInlineDataService} from '../../core/lib/inlineDataService';
import {SupportConversationsClient} from '../../core/lib/SupportConversationsClient';

type V1ConversationsControllerOptions = {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability: () => Observability;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
  getCurrentConversation?: () => Conversation | null | undefined;
  withHistory: boolean;
};

export type UniversalHelpControllerClient = Client;

type ConversationChannelState = {
  conversationId: string;
  channelConversationId: string;
  channel: ConversationChannel;
  state: 'active' | 'transitioning' | 'ended';
};

const unknownControllerError = (error: unknown): ControllerError => ({
  type: 'error',
  error: error instanceof Error ? error.message : 'Unknown controller error',
});

const success = <T>(data: T) => ({type: 'success' as const, data});

export class V1ConversationsController
  implements LegacyConversationsController
{
  private _inlineDataService: InlineDataService;

  private _inlineDataEventBus: InlineDataEventBus = new InlineDataEventBus();

  private _client: SupportConversationsClient;

  private _chatClient: Client;

  private _messagePartClient: MessagePartClient;

  private _api: ConversationsControllerApi;

  private _store: ConversationsStateStore;

  private _channels = new Map<string, ConversationChannelState>();

  constructor({
    getConfig,
    getProfile,
    getApi,
    getObservability,
    requestAction,
    getCurrentConversation,
    withHistory,
  }: V1ConversationsControllerOptions) {
    this._store = createStore<ConversationsState>({
      loadingState: {
        list: false,
        get: false,
        send: false,
      },
    });

    const config = getConfig();
    const {fetch} = getApi();

    if (withHistory) {
      clearStoredConversations({
        audience: config.audience,
        entryPoint: config.entryPoint,
      });
    }

    this._inlineDataService = newInlineDataService(fetch, {
      audience: config.audience,
      entry_point: config.entryPoint,
    });

    // The assistant messaging protocol is selected here: the v2 REST
    // conversations API client when enableConversationsApiV2 is on, the
    // legacy UniversalHelpClient otherwise.
    const buildClients = getProfile().featureFlags.enableConversationsApiV2
      ? buildV2Clients
      : buildV1Clients;
    const {client, chatClient, uhClient} = buildClients({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      inlineDataEventBus: this.inlineDataEventBus,
      requestAction,
      getCurrentConversation,
      withHistory,
    });

    this._chatClient = chatClient;

    this._client = client;

    this._messagePartClient = uhClient;

    this._api = buildV1ConversationsControllerApi({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      client: this._client,
    });
  }

  get inlineDataService(): InlineDataService {
    return this._inlineDataService;
  }

  get inlineDataEventBus(): InlineDataEventBus {
    return this._inlineDataEventBus;
  }

  get conversationsClient(): Client {
    return this._client;
  }

  get chatClient(): Client {
    return this._chatClient;
  }

  get messagePartClient(): MessagePartClient | undefined {
    return this._messagePartClient;
  }

  get stateStore(): ConversationsStateStore {
    return this._store;
  }

  get state(): ConversationsState {
    return this.stateStore.getState();
  }

  create = async (params?: CreateConversationParams) => {
    return this.withLoadingState('get', () => this._api.create(params));
  };

  get = async (
    id: string,
    {
      context,
    }: {
      context?: Partial<ConversationContext>;
    } = {},
  ) => {
    return this.withLoadingState('get', () => this._api.get(id, {context}));
  };

  list = async (filters?: ListConversationFilters) => {
    return this.withLoadingState('list', async () => {
      const response = await this._api.list(filters);

      if (response.type === 'error') {
        return response;
      }

      response.data.forEach((overview) => {
        const channelState: ConversationChannelState = {
          conversationId: overview.id,
          channelConversationId: overview.id,
          channel: 'bot',
          state: overview.is_active ? 'active' : 'ended',
        };

        if (overview.live_chat) {
          const {external_id: channelConversationId, status: state} =
            overview.live_chat;
          channelState.channelConversationId = channelConversationId;
          channelState.state = state;
          channelState.channel = 'chat';
        } else if (overview.request_a_call) {
          channelState.state =
            overview.request_a_call.status === 'queued' ? 'active' : 'ended';
          channelState.channel = 'request_a_call';
        }

        this.setConversationChannel(channelState);
      });

      return response;
    });
  };

  end = async (id: string) => {
    return this.withLoadingState('get', async () => {
      const response = await this._api.end(id);
      if (response.type === 'success') {
        clearStoredConversation(id);
      }

      return response;
    });
  };

  load = async (id: string) => {
    return this.withLoadingState('get', async () => {
      const listResponse = await this.list({conversationIds: [id]});
      if (listResponse.type === 'error') {
        return listResponse;
      }

      const overviews = listResponse.data;
      if (overviews.length === 0) {
        return unknownControllerError({
          message: 'load failed - overviews empty',
        });
      }

      const overview = overviews[0];

      // if the conversation is a non-bot conversations, this allows
      // the client to resume the conversation on the correct channel
      const context = this.getConversationChannelContext(id);

      const getResponse = await this.get(id, {context});
      if (getResponse.type === 'error') {
        return getResponse;
      }

      const conversation = getResponse.data;
      if (!conversation) {
        return unknownControllerError({
          message: 'load failed - conversation missing',
        });
      }

      return success({conversation, overview});
    });
  };

  createAndLoad = async (params?: CreateConversationParams) => {
    return this.withLoadingState('get', async () => {
      const response = await this.create(params);

      if (response.type === 'error') {
        return response;
      }

      const conversation = response.data;

      const listResponse = await this.list({
        conversationIds: [conversation.id],
      });

      if (listResponse.type === 'error') {
        return listResponse;
      }

      const overviews = listResponse.data;
      if (overviews.length === 0) {
        return unknownControllerError({
          message: 'conversations list returned empty result after create',
        });
      }

      const overview = overviews[0];

      return success({conversation, overview});
    });
  };

  private setConversationChannel = (channel: ConversationChannelState) => {
    // setting the channel state for both the canonical id and the channel conversation id
    this._channels.set(channel.conversationId, channel);
    this._channels.set(channel.channelConversationId, channel);
  };

  private getConversationChannelContext = (
    id: string,
  ): Partial<ConversationContext> | undefined => {
    const channelState = this._channels.get(id);
    if (channelState) {
      return {
        channel: channelState.channel,
        [channelState.channel]: channelState.channelConversationId,
        [`${channelState.channel}_conversationId`]:
          channelState.channelConversationId,
        bot_conversationId: channelState.conversationId,
      };
    }
  };

  private withLoadingState = async <T>(
    operation: ConversationLoadingOperation,
    task: () => ControllerResult<T>,
    onSuccess?: (data: T) => void,
  ): ControllerResult<T> => {
    this.stateStore.setValue((state) => {
      state.loadingState[operation] = true;
    });

    let result: Awaited<ControllerResult<T>>;
    try {
      result = await task();
    } catch (e) {
      result = unknownControllerError(e);
    }

    this.stateStore.setValue((state) => {
      state.loadingState[operation] = false;
      if (result.type !== 'error') {
        onSuccess?.(result.data);
      }
    });

    return result;
  };
}
